import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { StatusCodes } from 'http-status-codes';
import useApiClient from 'hooks/use-api-client';
import { useCallback } from 'react';
import type { BillingApi } from '@env0/billing-service/api';

const useCacheKeys = () => {
  const orgId = useCurrentOrganizationId();
  const baseKey = [orgId, 'billing-information'] as const;
  return {
    base: baseKey,
    usage: [orgId, 'billing-usage'] as const,
    usageHistory: [orgId, 'billing-usage-history'] as const,
    invoices: [orgId, 'billing-invoices'] as const
  };
};

export const useGetBillingInformation = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const apiClient = useApiClient();
  const { base } = useCacheKeys();
  const orgId = useCurrentOrganizationId();

  return useQuery({
    queryKey: base,
    queryFn: async ({ queryKey: [orgId] }) => {
      try {
        return await apiClient.billing.getBillingInformation(orgId);
      } catch (error) {
        // A missing row means the free tier: every organization is given one on creation. Any other
        // failure is surfaced — swallowing it told a paying customer they were free (ATH-474).
        if ((error as AxiosError).response?.status !== StatusCodes.NOT_FOUND) throw error;

        return { tier: 'basic' } as BillingApi.BillingInfo;
      }
    },
    // With retry off and staleTime Infinity, a reader mounting is what retries a failed read
    staleTime: Infinity,
    enabled: enabled && !!orgId
  });
};

export const useGetOrganizationUsage = () => {
  const apiClient = useApiClient();
  const { usage } = useCacheKeys();
  const orgId = useCurrentOrganizationId();

  return useQuery({
    queryKey: usage,
    queryFn: async ({ queryKey: [orgId] }) => await apiClient.billing.getUsage(orgId),
    enabled: !!orgId
  });
};

const useUsageHistoryQueryOptions = () => {
  const apiClient = useApiClient();
  const { usageHistory } = useCacheKeys();

  return queryOptions({
    queryKey: usageHistory,
    queryFn: async ({ queryKey: [orgId] }) => await apiClient.billing.getUsageHistory(orgId)
  });
};

// Its own request, so a slow read of the year behind the chart never holds up the meters (ATH-1038).
export const useGetOrganizationUsageHistory = ({ enabled }: { enabled: boolean }) => {
  const orgId = useCurrentOrganizationId();

  return useQuery({ ...useUsageHistoryQueryOptions(), enabled: enabled && !!orgId });
};

// For a reader that needs the series once, such as the CSV download: the cached ones, or a fetch.
export const useEnsureOrganizationUsageHistory = () => {
  const queryClient = useQueryClient();
  const options = useUsageHistoryQueryOptions();

  return async () => await queryClient.ensureQueryData(options);
};

export const useGetOrganizationInvoices = () => {
  const apiClient = useApiClient();
  const { invoices } = useCacheKeys();
  const orgId = useCurrentOrganizationId();

  return useQuery({
    queryKey: invoices,
    queryFn: async ({ queryKey: [orgId] }) => await apiClient.billing.getInvoices(orgId),
    enabled: !!orgId
  });
};

export const useGetManageSubscriptionLink = () => {
  const organizationId = useCurrentOrganizationId();
  const apiClient = useApiClient();
  return useCallback(
    async () => await apiClient.billing.getManageSubscriptionLink(organizationId),
    [apiClient.billing, organizationId]
  );
};
